import type { ArticleBlockFactBoxProps } from "./ShowArticleProps.d";

import React, {useEffect, useRef, useState} from 'react';

// TODO: Use RichText from @enonic/react-components
//       Requires all Macros to be reimplemented in React.
import RichText from "../../../react4xp/shared/Richtext/Richtext";
// import { RichText } from '@enonic/react-components';

import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';
import { faChevronRight, faChevronLeft } from "@fortawesome/pro-light-svg-icons";
import { Button } from '../../../react4xp/components/Button';
import { Link } from '@hdir/ui/link';


export const FactBlock: React.FC<ArticleBlockFactBoxProps> = ({ factCards }) => {
	if (factCards.length === 0) return null;

	const [currentIndex, setCurrentIndex] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [dragX, setDragX] = useState(0);
	const touchStartX = useRef(0);
	const touchMoveX = useRef(0);

	const [uniqueId, setUniqueId] = useState<string>();

	useEffect(() => {
		setUniqueId(`carousel-${Math.random().toString(36).slice(2, 11)}`); // Avoid hydration warning.
	}, []);

	const carouselRef = useRef<HTMLDivElement | null>(null);
	const [carouselContainer, setCarouselContainer] = useState<HTMLDivElement | null>(null);

	const nextSlide = (): void => {
		if (currentIndex < factCards.length - 1) {
			setCurrentIndex((prev) => prev + 1);

			if (carouselContainer) {
				carouselContainer.style.transform = `translateX(calc(-${(currentIndex + 1) * 100}% - ${(currentIndex + 1) * 16}px))`;
			}
		}
	};

	const prevSlide = (): void => {
		if (currentIndex > 0) {
			setCurrentIndex((prev) => prev - 1);

			if (carouselContainer) {
				carouselContainer.style.transform = `translateX(calc(-${(currentIndex - 1) * 100}% - ${(currentIndex - 1) * 16}px))`;
			}
		}
	};

	// Handle touch events
	const handleTouchStart = (e: React.TouchEvent): void => {
		touchStartX.current = e.touches[0].clientX;
		touchMoveX.current = touchStartX.current;
		setIsDragging(true);
	};

	const handleTouchMove = (e: React.TouchEvent): void => {
		const currentX = e.touches[0].clientX;
		touchMoveX.current = currentX;
		const deltaX = currentX - touchStartX.current;

		// Apply drag effect only if within bounds
		if (
			(currentIndex === 0 && deltaX > 0) ||
			(currentIndex === factCards.length - 1 && deltaX < 0)
		) {
			setDragX(deltaX / 4); // Reduce effect near edges
		} else {
			setDragX(deltaX);
		}
	};

	const handleTouchEnd = (): void => {
		setIsDragging(false);
		const deltaX = touchMoveX.current - touchStartX.current;
		const threshold = 50;

		if (deltaX > threshold && currentIndex > 0) {
			prevSlide();
		} else if (deltaX < -threshold && currentIndex < factCards.length - 1) {
			nextSlide();
		}

		setDragX(0); // Reset drag effect
	};

	useEffect(() => {
		if (carouselRef.current) {
			setCarouselContainer(carouselRef.current);
		}
	}, []);

	return (
		<>
			<section className="article relative w-full max-w-text"
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				<div className="w-full overflow-hidden">
					<div
						ref={carouselRef}
						className={`flex gap-md flex-grow transition-transform duration-300 ${isDragging ? "transition-none" : ""}`}
						aria-live="polite"
					>
						{factCards.map((card, index) => (
							<article key={index}
								id={`slide-${index}-${uniqueId}`}
								className={`p-xl bg-[#F5F3FC] grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] min-w-full gap-sm items-start rounded-sm ${factCards.length === 1 ? "mb-xl" : ""}`}
								aria-roledescription="slide"
								aria-labelledby="carouselheading"
								tabIndex={-1}
							>
								<svg className="w-max" xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" fill="none">
									<svg className="w-max" xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" fill="none" aria-hidden="true">
										<g clipPath="url(#clip0_3037_48382)">
											<path d="M1.725 0.0530542C1.47812 -0.0719458 1.17813 0.0311792 1.05313 0.278054C0.928125 0.524929 1.03125 0.824929 1.27813 0.949929L3.27813 1.94993C3.525 2.07493 3.825 1.9718 3.95 1.72493C4.075 1.47805 3.97187 1.17805 3.725 1.05305L1.725 0.0530542ZM18.725 0.946804C18.9719 0.821804 19.0719 0.521804 18.95 0.274929C18.8281 0.0280542 18.525 -0.0719458 18.2781 0.0499292L16.2781 1.04993C16.0312 1.17493 15.9313 1.47493 16.0531 1.7218C16.175 1.96868 16.4781 2.06868 16.725 1.9468L18.725 0.946804ZM0.5 4.99993C0.225 4.99993 0 5.22493 0 5.49993C0 5.77493 0.225 5.99993 0.5 5.99993H2.5C2.775 5.99993 3 5.77493 3 5.49993C3 5.22493 2.775 4.99993 2.5 4.99993H0.5ZM17.5 4.99993C17.225 4.99993 17 5.22493 17 5.49993C17 5.77493 17.225 5.99993 17.5 5.99993H19.5C19.775 5.99993 20 5.77493 20 5.49993C20 5.22493 19.775 4.99993 19.5 4.99993H17.5ZM3.725 9.9468C3.97187 9.8218 4.07188 9.5218 3.95 9.27493C3.82812 9.02805 3.525 8.92805 3.27813 9.04993L1.27813 10.0499C1.03125 10.1749 0.93125 10.4749 1.05313 10.7218C1.175 10.9687 1.47812 11.0687 1.725 10.9468L3.725 9.9468ZM16.725 9.05305C16.4781 8.92805 16.1781 9.03118 16.0531 9.27805C15.9281 9.52493 16.0312 9.82493 16.2781 9.94993L18.2781 10.9499C18.525 11.0749 18.825 10.9718 18.95 10.7249C19.075 10.4781 18.9719 10.1781 18.725 10.0531L16.725 9.05305ZM14.5 5.49993C14.5 6.45618 14.2031 7.33743 13.6969 8.06555C13.5687 8.24993 13.425 8.44993 13.2719 8.6593C12.875 9.20618 12.425 9.82181 12.0844 10.4437C11.8063 10.9499 11.6562 11.4843 11.5781 12.0031H12.5906C12.6594 11.6281 12.775 11.2624 12.9594 10.9249C13.2688 10.3624 13.6531 9.8343 14.0375 9.30618C14.2 9.0843 14.3625 8.86243 14.5188 8.63743C15.1375 7.7468 15.5 6.66868 15.5 5.50305C15.5 2.46243 13.0375 -7.08068e-05 10 -7.08068e-05C6.9625 -7.08068e-05 4.5 2.46243 4.5 5.49993C4.5 6.66555 4.8625 7.7468 5.48125 8.6343C5.6375 8.8593 5.8 9.08118 5.9625 9.30305C6.34688 9.82805 6.73125 10.3562 7.04063 10.9218C7.225 11.2593 7.34063 11.6249 7.40938 11.9999H8.42188C8.34375 11.4812 8.19375 10.9468 7.91563 10.4406C7.575 9.81555 7.125 9.19993 6.72813 8.65618C6.575 8.4468 6.43125 8.24993 6.30312 8.06243C5.79688 7.33743 5.5 6.45618 5.5 5.49993C5.5 3.01555 7.51562 0.999929 10 0.999929C12.4844 0.999929 14.5 3.01555 14.5 5.49993ZM7.5 5.49993C7.5 4.11868 8.61875 2.99993 10 2.99993C10.275 2.99993 10.5 2.77493 10.5 2.49993C10.5 2.22493 10.275 1.99993 10 1.99993C8.06563 1.99993 6.5 3.56555 6.5 5.49993C6.5 5.77493 6.725 5.99993 7 5.99993C7.275 5.99993 7.5 5.77493 7.5 5.49993ZM10 14.9999C9.34688 14.9999 8.79063 14.5812 8.58438 13.9999H11.4125C11.2063 14.5812 10.65 14.9999 9.99687 14.9999H10ZM7.5 13.3343V13.4999C7.5 14.8812 8.61875 15.9999 10 15.9999C11.3813 15.9999 12.5 14.8812 12.5 13.4999V13.3343C12.5 13.1499 12.35 12.9999 12.1656 12.9999H7.83125C7.64688 12.9999 7.49687 13.1499 7.49687 13.3343H7.5Z" fill="black"/>
										</g>
										<defs>
											<clipPath id="clip0_3037_48382">
												<rect width="20" height="16" fill="white"/>
											</clipPath>
										</defs>
									</svg>
								</svg>
								<h2 className="text-lg leading-tight font-bold" id="carouselheading">{card.title}</h2>
								<div className="flex flex-col col-start-2 gap-xs items-start justify-start flex-grow h-full">
									<RichText className="article__rich-text-container--info" text={card.text} />
									{card.link && (
										<Link
											href={card.link.link?.href}
											tabIndex={currentIndex === index ? 0 : -1}
										>
											{card.link.linkText}
										</Link>
									)}
								</div>
							</article>
						))}
					</div>
				</div>
				{factCards.length > 1 && (
					<>
						{currentIndex > 0 &&
							<Button
								className="absolute left-0 p-xs top-1/2 !-translate-y-1/2 !-translate-x-1/4 bg-gray-200 p-2 rounded-full border-action-secondary-default"
								onClick={prevSlide}
								variant="icon"
								aria-label="Gå til forrige side i faktaboksen"
							>
								<FontAwesomeIconWrapper icon={faChevronLeft} fixedWidth={true} />
							</Button>
						}
						{currentIndex < factCards.length - 1 &&
							<Button
								className="absolute right-0 p-xs top-1/2 !-translate-y-1/2 !translate-x-1/4 bg-gray-200 p-2 rounded-full"
								onClick={nextSlide}
								variant="icon"
								aria-label="Gå til neste side i faktaboksen"
							>
								<FontAwesomeIconWrapper icon={faChevronRight} fixedWidth={true} />
							</Button>
						}
						<div className="flex justify-center items-center w-full max-w-text mt-xs mb-xl" role="list">
							{factCards.map((item, index): React.JSX.Element =>
								<div key={index}
									className="p-xs rounded-full leading-[8px] flex justify-center items-center"
									role="listitem"
									aria-label={`Kort ${index + 1} av ${factCards.length}`}
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
										<circle cx="3.69565" cy="3.69565" r="3.69565" fill={currentIndex === index ? "#767676" : "#D8DADA"} />
									</svg>
								</div>,
							)}
						</div>
					</>
				)}
			</section>
		</>
	);
};
